import { ResourceContainer, SceneManager } from 'ohzi-core';

import { FrontSide, Mesh, MeshStandardMaterial, PlaneGeometry, PointLight, sRGBEncoding } from 'three';
import { Shape } from 'three';
import { ExtrudeGeometry } from 'three';
import { BackSide } from 'three';
import { BoxGeometry } from 'three';
import { MeshBasicMaterial } from 'three';

import GlassFrontMaterial from '../materials/GlassFrontMaterial';
import TexturedGlassFrontMaterial from '../materials/TexturedGlassFrontMaterial';
import GlassBackMaterial from '../materials/GlassBackMaterial';
import GlassSideMaterial from '../materials/GlassSideMaterial';
import GenericStructureElement from './GenericStructureElement';
import AluminiumTapeMaterial from './../materials/AluminiumTapeMaterial';
import OhziLabels from './OhziLabels';

export default class GenericGlass extends GenericStructureElement {

  constructor(structure, rt, render_order_offset, is_frg) {
    super();

    this.structure = structure;
    this.is_frg = is_frg;

    const alu_tape_thickness = is_frg ? 0.002 : 0;
    const bias = 0.0001;

    const extrude = 0.01;
    const length = 2, height = 2, cut = 1 - extrude * 2;
    const depth = 1;
    const shape = new Shape();
    shape.moveTo(0 + alu_tape_thickness, 0);
    shape.lineTo(0 + alu_tape_thickness, height - alu_tape_thickness - extrude * 2);
    shape.lineTo(length - cut, height - alu_tape_thickness - extrude * 2);
    shape.lineTo((length - extrude * 2), height - alu_tape_thickness - extrude * 2 - cut);
    shape.lineTo((length - extrude * 2), 0);
    shape.lineTo(0 + alu_tape_thickness, 0);

    const extrudeSettings = {
      steps: 1,
      depth: depth - extrude * 2,
      bevelEnabled: true,
      bevelThickness: extrude,
      bevelSize: extrude,
      bevelOffset: 0,
      bevelSegments: 1
    };

    const main_geometry = new ExtrudeGeometry(shape, extrudeSettings);
    main_geometry.translate(extrude + alu_tape_thickness, extrude + alu_tape_thickness + bias, extrude);
    main_geometry.computeVertexNormals();
    this.frontMaterial = new GlassFrontMaterial(rt);
    this.side_material = new GlassSideMaterial(rt);
    this.texturedFrontMaterial = new TexturedGlassFrontMaterial();
    this.texturedFrontMaterial.transparent = true;
    this.texturedFrontMaterial.depthWrite = true;

    const backMaterial = new GlassBackMaterial();
    backMaterial.transparent = true;

    this.render_order_offset = render_order_offset;
    this.cube = new Mesh(main_geometry, [this.frontMaterial, this.side_material]);
    this.cube.renderOrder = 1000 + render_order_offset;

    this.frontMaterial_back = new GlassFrontMaterial(rt);
    this.side_material_back = new GlassSideMaterial(rt);
    this.frontMaterial_back.side = BackSide;
    this.side_material_back.side = BackSide;
    this.frontMaterial_back.transparent = true;
    this.side_material_back.transparent = true;

    this.cube_back = new Mesh(main_geometry, [this.frontMaterial_back, this.side_material_back]);
    this.cube_back.renderOrder = 1001 + render_order_offset;
    // above 999: inner glass frame is white
    // below 999: textures are not shown when viewed through other glasses, only when viewed directly

    let back_cube = this.cube.clone();
    back_cube.material = backMaterial;
    this.add(this.cube);
    this.cube.add(this.cube_back);

    // for the glass to cast shadows
    this.shadow_cube = this.cube_back.clone();
    this.shadow_cube.material = new MeshBasicMaterial({ opacity: 0, transparent: true });
    this.shadow_cube.material.colorWrite = false;
    this.shadow_cube.material.depthWrite = false;
    this.shadow_cube.receiveShadow = true;
    this.shadow_cube.castShadow = true;

    SceneManager.backScene.add(back_cube);
    SceneManager.current.add(this.shadow_cube);

    // alu tape around frg
    if (is_frg) {
      const alu_tape_geometry_x = new BoxGeometry(length, alu_tape_thickness, depth);
      alu_tape_geometry_x.translate(length * 0.5, alu_tape_thickness * 0.5, depth * 0.5);

      this.alu_tape = new Mesh(alu_tape_geometry_x, new AluminiumTapeMaterial());
      this.alu_tape.receiveShadow = true;
      this.alu_tape.castShadow = true;
      this.alu_tape.renderOrder = 1000 + render_order_offset;
      this.cube.add(this.alu_tape);

      const alu_tape_geometry_y = new BoxGeometry(length, alu_tape_thickness, depth);
      alu_tape_geometry_y.rotateZ(Math.PI * 0.5);
      alu_tape_geometry_y.translate(alu_tape_thickness * 0.5, length * 0.5, depth * 0.5);

      this.alu_tape_y = new Mesh(alu_tape_geometry_y, new AluminiumTapeMaterial());
      this.alu_tape_y.receiveShadow = true;
      this.alu_tape_y.castShadow = true;
      this.alu_tape_y.renderOrder = 1000 + render_order_offset;
      this.cube.add(this.alu_tape_y);

      const frontPointLight = new PointLight(0xffffff, 1, 200);
      frontPointLight.position.set(1.45, 1.45, 4);
      this.cube.add(frontPointLight);

      const backPointLight = new PointLight(0xffffff, 1, 200);
      backPointLight.position.set(1.45, 1.45, -4);
      this.cube.add(backPointLight);

      this.applySigab002Texture();
    }

    this.label = new OhziLabels(null, 0.1);
    this.label.position.y = -0.1;
    this.label.position.x = length;
    this.label.renderOrder = 1000000 + render_order_offset;
    this.label.visible = this.is_frg;
    SceneManager.textScene.add(this.label);

    this.set(0.01, ['.01:0']);
  }

  setReverseRenderOrder(rev) {
    const fact = rev ? 1 : -1;
    this.cube.renderOrder = 1000 + (fact * this.render_order_offset);
    this.cube_back.renderOrder = 1001 +  (fact * this.render_order_offset);
  }

  applySigab002Texture() {
    const sigab002Texture = ResourceContainer.get_resource('sigab002');
    sigab002Texture.encoding = sRGBEncoding;

    const applyTextureToCube = (cube, textureOnBack) => {
      const maxTexHeight = .1;
      const texAspect = sigab002Texture.image.width / sigab002Texture.image.height;
      const texWidth = maxTexHeight * texAspect;

      const texMaterial = new MeshStandardMaterial({
        map: sigab002Texture,
        transparent: true,
        opacity: 0.95,
        roughness: 0.5,
        metalness: 0.05,
        depthTest: true,
        depthWrite: false,
        side: textureOnBack ? FrontSide : BackSide,
      });

      // Calculate size and position for the logo
      const texGeometry = new PlaneGeometry(texWidth, maxTexHeight);
      const texMesh = new Mesh(texGeometry, texMaterial);
      texMesh.renderOrder = 1002;

      const zOffset = -0.022; // slight offset to avoid z-fighting
      texMesh.position.set(
        1.45, 1.45,
        textureOnBack ? -(zOffset - 1) : zOffset
      );
      texMesh.rotateZ(Math.PI / -4.0); // -45°

      // Mirror the logo on the Z-axis if it's on the back
      if (!textureOnBack) {
        texMesh.scale.set(-1, 1, 1); // Flips the mesh on the X-axis
      }

      cube.add(texMesh);
    };

    // Apply logo to both long and short alu tape
    applyTextureToCube(this.cube, false);
    applyTextureToCube(this.cube_back, true);
  }

  update() {
    this.side_material.update();
    this.frontMaterial.update();
  }

  fade_animation(lerp) {
    this.cube.position.y = lerp * 0.5;
    this.cube.position.x = lerp * 0.5;
    this.frontMaterial.opacity = 1 - lerp;
    this.side_material.opacity = 1 - lerp;
  }

  set(width, layers, flip)
  {
    if (!layers)
      layers = [width + ':0'];

    if (!this.is_frg || width < 1 || this.structure.measurementArrows.visible) {
      this.label.visible = false;
    }
    else {
      this.label.visible = true;
      this.label.text = '' + width.toFixed(0);
    }

    this._layers = layers;

    if (width < 0.01) {
      this.visible = false;
    }
    else {
      this.frontMaterial.glassThickness = width;
      this.set_front_material(layers);
      this.side_material.set(width, layers, flip);
      this.side_material_back.set(width, layers);
      this.visible = true;
    }

    super.set(width);

    this.shadow_cube.scale.z = this.scale.z;
    this.shadow_cube.position.z = this.position.z;
  }

  set_front_material(layers) {
    if (layers.find((layer) => {
      const spl1 = layer.split(':')[1]
      if (spl1 == 100) {
        this.texturedFrontMaterial.set(0);
        return true;
      }
      if (spl1 == 101) {
        this.texturedFrontMaterial.set(1);
        return true;
      }
      if (spl1 == 102) {
        this.texturedFrontMaterial.set(2);
        return true;
      }
      if (spl1 == 103) {
        this.texturedFrontMaterial.set(3);
        return true;
      }
      return false;
    })) {
      this.cube.material[0] = this.texturedFrontMaterial;
    }
    else {
      this.cube.material[0] = this.frontMaterial;
    }
  }

  set shiftZ(shift) {
    super.shiftZ = shift;
    this.label.position.z = shift - 0.03 - (this.structure.structure_width || 0 / 2.0) + (this.frontMaterial.glassThickness / 110.0);
  }
}
